import { Chip } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { universalWasteCategoryValue } from "components/waste/complianceEngine/differences";
import { ExplainClassificationButton } from "components/waste/complianceEngine/ExplainClassificationButton";
import { wasteKindConclusionLabel } from "components/waste/complianceEngine/explainedFields";
import { PipelineRowClassificationDialog } from "components/waste/complianceEngine/PipelineRowClassificationDialog";
import { WastePipelineRunRowsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { prettyPrintWasteClassification } from "routes/Customer/Waste/Facilities/Facility/prettyPrintWasteClassification";
import { explainClassificationSubtitle } from "routes/Customer/Waste/Facilities/Facility/WasteStreamDetails/buildExplainClassificationColumn";

export type ReviewRow = WastePipelineRunRowsQuery["wastePipelineRunRows"]["items"][number];

export function renderReportableChip({ value }: GridRenderCellParams) {
  if (!value) return null;
  return (
    <Chip
      label={value}
      size="small"
      variant="outlined"
      color={value === "Reportable" ? "success" : "default"}
      sx={{ pointerEvents: "none" }}
    />
  );
}

export const ENGINE_PREVIEW_NOTE =
  "Compliance Engine preview columns show what the engine derives from each row's waste codes, " +
  "description and management method for the matched facility. They are computed on read and never saved. " +
  "A filled Reportable or Classification column on the row overrides the engine's verdict at ingestion.";

export function enginePreviewColumns(): OmnisearchGridColDef<ReviewRow>[] {
  const computedOnRead = { editable: false, sortable: false, filterable: false, minWidth: 170 };
  return [
    {
      ...computedOnRead,
      field: "engineClassification",
      headerName: "CE Classification (preview)",
      description: ENGINE_PREVIEW_NOTE,
      valueFormatter: (value: ReviewRow["engineClassification"]) =>
        prettyPrintWasteClassification(value),
    },
    {
      ...computedOnRead,
      field: "engineReportable",
      headerName: "CE Reportable (preview)",
      description: ENGINE_PREVIEW_NOTE,
      renderCell: renderReportableChip,
    },
    {
      ...computedOnRead,
      field: "engineWasteKind",
      headerName: "CE Waste Kind (preview)",
      description: ENGINE_PREVIEW_NOTE,
      valueFormatter: (value: ReviewRow["engineWasteKind"]) =>
        value == null ? "" : wasteKindConclusionLabel(value),
    },
    {
      ...computedOnRead,
      field: "engineUniversalWasteCategory",
      headerName: "CE Universal Waste Category (preview)",
      description: ENGINE_PREVIEW_NOTE,
      minWidth: 220,
      valueFormatter: (value: ReviewRow["engineUniversalWasteCategory"]) =>
        value == null ? "" : universalWasteCategoryValue(value),
    },
  ];
}

const hasEngineVerdict = (row: ReviewRow) => row.engineClassification != null;

export function engineExplainColumn(): OmnisearchGridColDef<ReviewRow> {
  return {
    field: "explainClassification",
    headerName: "",
    editable: false,
    sortable: false,
    filterable: false,
    width: 56,
    align: "center",
    renderCell: ({ row }: { row: ReviewRow }) =>
      hasEngineVerdict(row) ? (
        <ExplainClassificationButton
          renderDialog={(onClose) => (
            <PipelineRowClassificationDialog
              open
              row={row}
              onClose={onClose}
              subtitle={explainClassificationSubtitle(row)}
            />
          )}
        />
      ) : null,
  };
}
